/**
 * Axios client — behavioural port of rent_management_app/lib/core/api/api_client.dart.
 *
 * Reproduced from the Dio client:
 *   - request interceptor attaches `Authorization: Bearer <access>` when a token
 *     is stored (api_client.dart:37-48);
 *   - response interceptor on 401 — EXCLUDING the refresh endpoint itself, to
 *     avoid an infinite loop — refreshes and retries the original request ONCE
 *     (api_client.dart:50-77);
 *   - on refresh failure, clears storage and forces logout (api_client.dart:71-74).
 *
 * Two deliberate improvements over the literal port are marked below:
 * SINGLE-FLIGHT REFRESH and REFRESH-TOKEN ROTATION.
 */
import axios, {
  AxiosHeaders,
  type AxiosAdapter,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearSession, getAccessToken, getRefreshToken, setTokens } from '../auth/storage';
import { AUTH_REFRESH } from './endpoints/paths';

/**
 * Vite requires a literal `import.meta.env.VITE_*` reference (it string-replaces
 * at build time), so this cannot be read dynamically.
 */
const rawBaseUrl: string = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Axios joins `baseURL` and a relative `url` with no separator. */
export const API_BASE_URL: string = rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // AppConstants.connectTimeout / receiveTimeout are 60s each (constants.dart:74-75).
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * The refresh call goes through a SEPARATE, interceptor-free instance.
 *
 * Dio needs only a path check because it runs one interceptor chain; using the
 * main instance here would make a 401 from the refresh endpoint re-enter the
 * error interceptor. The path check below is kept as well — belt and braces,
 * since a caller could still invoke `apiClient.post(AUTH_REFRESH, ...)` directly.
 */
const refreshClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// ---------------------------------------------------------------------------
// Forced logout
// ---------------------------------------------------------------------------

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * Registered by AuthContext so a failed refresh can drop React state, not just
 * localStorage. The Dio client only calls `_storage.clear()`, which leaves the
 * in-memory session live until the next navigation; this closes that gap.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

function forceLogout(): void {
  clearSession();
  onUnauthorized?.();
}

// ---------------------------------------------------------------------------
// Request interceptor
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token !== null) {
    // Axios v1 requires headers to stay an AxiosHeaders instance.
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Refresh, with a single-flight lock
// ---------------------------------------------------------------------------

/**
 * SINGLE-FLIGHT REFRESH — a deliberate improvement over the Flutter client.
 *
 * Dio does not need this. Its interceptor chain is serialised per Dio instance,
 * so while `_onError` is awaiting `_refreshToken()` no other response can be
 * processed by the same instance, and the second 401 finds a fresh token
 * already in storage.
 *
 * Axios has no such serialisation: every interceptor rejection is handled
 * concurrently. A dashboard that fires six parallel queries on mount with an
 * expired access token would otherwise produce SIX simultaneous
 * `POST auth/refresh/` calls. With ROTATE_REFRESH_TOKENS +
 * BLACKLIST_AFTER_ROTATION on the backend, the first call blacklists the
 * presented refresh token, so the other five present an
 * already-blacklisted token and 401 — logging the user out mid-session, at
 * random, under nothing more exotic than a normal page load.
 *
 * The lock is the standard queued-promise pattern: the first caller starts the
 * refresh and stores the in-flight promise; concurrent callers await that same
 * promise instead of starting their own. The slot is cleared once it settles so
 * a later expiry can refresh again.
 */
let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refresh = getRefreshToken();
  if (refresh === null || refresh === '') {
    throw new Error('No refresh token stored');
  }

  const response = await refreshClient.post<{ access: string; refresh: string }>(AUTH_REFRESH, {
    refresh,
  });

  // REFRESH-TOKEN ROTATION — the second deliberate improvement. The endpoint
  // returns a NEW pair and blacklists the one just presented
  // (refactor/backend/src/modules/auth/routes.ts:243-254). The Flutter client
  // persists only `access` (api_client.dart:90-94), so its next refresh presents
  // a blacklisted token and fails. Both are persisted here.
  setTokens({ access: response.data.access, refresh: response.data.refresh });
  return response.data.access;
}

function refreshAccessToken(): Promise<string> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Response interceptor: 401 -> refresh -> retry once
// ---------------------------------------------------------------------------

/** Marker so a retried request can never itself trigger another retry. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _isRetry?: boolean;
}

function isRefreshRequest(config: InternalAxiosRequestConfig): boolean {
  return (config.url ?? '').includes(AUTH_REFRESH);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    // `axios.isAxiosError`, not `instanceof AxiosError`: the instanceof check
    // fails when more than one copy of axios ends up in the bundle (the error is
    // then built by a different class object). The duck-type check is what axios
    // itself recommends.
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const config = error.config as RetriableConfig | undefined;

    if (
      error.response?.status !== 401 ||
      config === undefined ||
      config._isRetry === true ||
      isRefreshRequest(config)
    ) {
      return Promise.reject(error);
    }

    config._isRetry = true;

    let access: string;
    try {
      access = await refreshAccessToken();
    } catch {
      // Refresh failed (missing, expired, or already-blacklisted refresh token):
      // clear storage and force logout, then propagate the ORIGINAL 401 — the
      // caller cares about the request it made, not about the refresh attempt.
      forceLogout();
      return Promise.reject(error);
    }

    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${access}`);
    config.headers = headers;

    // A failure here propagates as-is: `_isRetry` stops it from looping.
    return apiClient.request(config);
  },
);

/**
 * Test-only reset. Vitest reuses one module instance across cases in a file, so
 * the in-flight slot has to be clearable between them.
 */
export function __resetRefreshStateForTests(): void {
  refreshInFlight = null;
}

/**
 * Test-only seam. Points BOTH instances at a stub adapter — `refreshClient` is
 * deliberately not exported, but the refresh path is the most important thing
 * to test, so tests need some way to intercept it. Setting the adapter is the
 * lowest-level seam axios offers short of a real network.
 */
export function __setTestAdapter(adapter: AxiosAdapter | undefined): void {
  if (adapter === undefined) {
    delete apiClient.defaults.adapter;
    delete refreshClient.defaults.adapter;
    return;
  }
  apiClient.defaults.adapter = adapter;
  refreshClient.defaults.adapter = adapter;
}

/**
 * `FormData` upload wrapper — mirrors `api_client.dart`'s `uploadFile()`.
 *
 * The `Content-Type: application/json` header set on `apiClient` at creation
 * is a per-instance DEFAULT, and axios's own "let the browser set the
 * multipart boundary" auto-detection only fires when no `Content-Type` was
 * already explicitly configured — an instance default counts as explicit. So
 * the default has to be cleared per request; `undefined` here removes the
 * header from this one request rather than sending the literal string
 * `"undefined"`, letting the browser generate the correct
 * `multipart/form-data; boundary=...` value itself.
 */
export async function uploadFile<T>(url: string, formData: FormData): Promise<T> {
  const response = await apiClient.post<T>(url, formData, {
    // `null`, not `undefined`: `AxiosHeaderValue` admits `null` but not
    // `undefined`, and axios's `normalizeValue` treats both identically
    // (`value == null`) — so this clears the instance's default
    // `Content-Type: application/json` for just this request, letting the
    // browser set the correct `multipart/form-data; boundary=...` value.
    headers: { 'Content-Type': null },
  });
  return response.data;
}

export default apiClient;
